import type { DrugSortKey } from '@/entities/drug/api/drug.api';
import { useRecentSearches } from '@/entities/drug/hooks/useRecentSearches';
import { PageHeader } from '@/shared/components/PageHeader';
import { Button } from '@/shared/components/ui/button';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { getRouteApi } from '@tanstack/react-router';
import type { DrugFilterValue } from './components/DrugFilterRail';
import { DrugSearchBox } from './components/DrugSearchBox';
import { DrugSearchResults } from './components/DrugSearchResults';
import { RecentSearchesCard } from './components/RecentSearchesCard';

const route = getRouteApi('/app/drugs');

/**
 * M-10 검색 전 / M-11 검색 결과 — q(또는 거래 가능 필터)가 있으면 결과 화면
 * 검색어 · 필터 · 정렬 · 페이지는 모두 URL search params에 둔다 (새로고침 · 공유 시 복원).
 * API: GET /web/drugs, /web/drugs/pharma-companies, /web/drugs/suggestions
 */
export const DrugsPage = () => {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const recent = useRecentSearches();

  const keyword = search.q ?? '';
  const filter: DrugFilterValue = {
    tradable: !!search.tradable,
    filters: search.filters ?? [],
    pharma: search.pharma ?? [],
  };

  /** 검색 실행 — 필터 · 정렬은 유지하고 페이지만 처음으로 */
  const runSearch = (q: string) => {
    recent.add(q);
    void navigate({
      to: ROUTE_PATHS.DRUGS,
      search: (prev) => ({ ...prev, q, page: undefined, drug: undefined }),
    });
  };

  const setFilter = (next: DrugFilterValue) =>
    void navigate({
      search: (prev) => ({
        ...prev,
        tradable: next.tradable || undefined,
        filters: next.filters.length ? next.filters : undefined,
        pharma: next.pharma.length ? next.pharma : undefined,
        page: undefined,
      }),
    });

  const setSort = (sort: DrugSortKey) =>
    void navigate({
      search: (prev) => ({ ...prev, sort, page: undefined }),
    });

  const setPage = (page: number) => {
    void navigate({ search: (prev) => ({ ...prev, page }) });
    window.scrollTo({ top: 0 });
  };

  /**
   * [나의 의약품 보기] — "거래 가능 필터"가 켜진 M-11을 연다 (M-10 기획).
   * 검색 API는 keyword가 필수라 검색어 없이 결과를 부를 수 없다.
   * 그래서 거래 가능 필터만 켠 결과 화면으로 보내고 검색창에 포커스를 둔다.
   * 검색어가 있던 상태면 그 검색어 그대로 필터만 켠다.
   */
  const showMyDrugs = () =>
    void navigate({
      search: (prev) => ({ ...prev, tradable: true, page: undefined }),
    });

  // M-11 결과 화면
  if (search.q || search.tradable) {
    return (
      <div className="flex flex-col gap-5 lg:-mt-1">
        <DrugSearchBox
          key={keyword}
          variant="bar"
          defaultValue={keyword}
          autoFocus={!keyword}
          onSubmit={runSearch}
        />
        <DrugSearchResults
          keyword={keyword}
          filter={filter}
          sort={search.sort ?? 'DRUG_NAME_ASC'}
          page={search.page ?? 1}
          openedDrug={search.drug}
          onFilterChange={setFilter}
          onSortChange={setSort}
          onPageChange={setPage}
        />
      </div>
    );
  }

  // M-10 검색 전
  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader
        title="의약품 검색"
        description="제품명 · 성분명 · 제약사로 약가와 수수료율을 확인하세요."
        actions={
          <Button
            variant="secondary"
            size="md"
            className="h-10 px-3.5 text-ink-2"
            onClick={showMyDrugs}
          >
            나의 의약품 보기
          </Button>
        }
      />
      <div className="flex flex-col gap-5">
        <section className="flex flex-col items-center rounded-[18px] border border-line bg-surface px-4 pt-8 pb-7 lg:px-10 lg:pt-10 lg:pb-9">
          <h2 className="mb-[18px] text-center text-lg leading-[30px] font-bold text-ink lg:text-xl">
            찾으시는 의약품을 입력하세요
          </h2>
          <DrugSearchBox variant="hero" autoFocus onSubmit={runSearch} />
        </section>
        <div className="flex justify-center">
          <RecentSearchesCard
            items={recent.items}
            onPick={runSearch}
            onRemove={recent.remove}
            onClear={recent.clear}
          />
        </div>
      </div>
    </div>
  );
};
